"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SurplusCalculator = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_dom_1 = require("react-dom");
const styled_components_1 = __importDefault(require("styled-components"));
const theme_1 = require("../../theme");
const calculator_styles_1 = require("./calculator-styles");
const Assumptions_1 = require("./Assumptions");
const HeadlineFigures_1 = require("./HeadlineFigures");
const InputsPanel_1 = require("./InputsPanel");
const LedgerPanel_1 = require("./LedgerPanel");
const QuoteRequestCta_1 = require("./QuoteRequestCta");
const BiofuelOfferCta_1 = require("./BiofuelOfferCta");
const SensitivityPanel_1 = require("./SensitivityPanel");
const model_1 = require("./model");
const useMarketQuotes_1 = require("./useMarketQuotes");
const storage_1 = require("./storage");
const consent_1 = require("./consent");
const netlifyForms_1 = require("./netlifyForms");
const PrintReport_1 = require("./PrintReport");
const EMPTY_NOTE = { text: "", warn: false };
/** Dimmed while the figures on screen no longer match the form. */
const Results = styled_components_1.default.div `
  opacity: ${(p) => (p.$stale ? 0.5 : 1)};
  transition: opacity 0.18s ease;
`;
const StaleNotice = styled_components_1.default.div `
  display: flex;
  align-items: center;
  gap: ${theme_1.theme.spacing(2)};
  background: ${calculator_styles_1.ATTENTION_WASH};
  border: 1px solid ${calculator_styles_1.ATTENTION};
  border-radius: 4px;
  padding: ${theme_1.theme.spacing(2)} ${theme_1.theme.spacing(3)};
  margin-bottom: ${theme_1.theme.spacing(3)};
  ${theme_1.theme.fontNormal};
  ${theme_1.theme.fontSize(0)};
  color: #8a6200;
  line-height: 1.5;

  b {
    ${theme_1.theme.fontLabelBold};
  }
`;
const StaleDot = styled_components_1.default.span `
  flex: 0 0 auto;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${calculator_styles_1.ATTENTION};
`;
const SurplusCalculator = () => {
    const [stored] = (0, react_1.useState)(storage_1.loadInputs);
    // `raw`/`fossil` are what the form shows; `committed` is what the results are
    // built from, so typing never makes the figures jump around mid-edit.
    const [fossil, setFossil] = (0, react_1.useState)(stored.fossil);
    const [raw, setRaw] = (0, react_1.useState)(stored.raw);
    const [committed, setCommitted] = (0, react_1.useState)(stored);
    const [consent, setConsent] = (0, react_1.useState)(consent_1.loadConsent);
    const [openHelp, setOpenHelp] = (0, react_1.useState)(null);
    const [email, setEmail] = (0, react_1.useState)("");
    const [phone, setPhone] = (0, react_1.useState)("");
    const [topics, setTopics] = (0, react_1.useState)([]);
    const [note, setNote] = (0, react_1.useState)(EMPTY_NOTE);
    const [sendState, setSendState] = (0, react_1.useState)("idle");
    const [fuelEmail, setFuelEmail] = (0, react_1.useState)("");
    const [fuelPhone, setFuelPhone] = (0, react_1.useState)("");
    const [imo, setImo] = (0, react_1.useState)("");
    const [fuelNote, setFuelNote] = (0, react_1.useState)(EMPTY_NOTE);
    const [fuelSendState, setFuelSendState] = (0, react_1.useState)("idle");
    const ctaRef = (0, react_1.useRef)(null);
    const emailRef = (0, react_1.useRef)(null);
    const focusTimer = (0, react_1.useRef)(undefined);
    (0, react_1.useEffect)(() => () => window.clearTimeout(focusTimer.current), []);
    // Everything typed by hand is kept locally so a return visit starts filled in.
    (0, react_1.useEffect)(() => (0, storage_1.saveInputs)(fossil, raw), [fossil, raw]);
    const inputs = (0, react_1.useMemo)(() => (0, model_1.toInputs)(committed.fossil, committed.raw), [committed]);
    const result = (0, react_1.useMemo)(() => (0, model_1.calculate)(inputs), [inputs]);
    const dirty = fossil !== committed.fossil ||
        JSON.stringify(raw) !== JSON.stringify(committed.raw);
    const handleCalculate = () => {
        const next = { fossil, raw };
        setCommitted(next);
        if (consent === "granted") {
            const nextInputs = (0, model_1.toInputs)(fossil, raw);
            void (0, netlifyForms_1.submitInputs)(nextInputs, (0, model_1.calculate)(nextInputs));
        }
    };
    /*
     * The report is portalled to <body> because the whole interactive shell is
     * hidden for print, and anything rendered inside it would be hidden too.
     */
    const report = (0, react_dom_1.createPortal)((0, jsx_runtime_1.jsx)(PrintReport_1.PrintReport, { inputs: inputs, result: result }), document.body);
    const handleConsent = (granted) => {
        const state = granted ? "granted" : "denied";
        setConsent(state);
        (0, consent_1.saveConsent)(state);
    };
    const applyQuotes = (0, react_1.useCallback)((quotes) => {
        const patch = {
            ...(quotes.eua !== undefined ? { eua: String(quotes.eua) } : {}),
            ...(quotes.fx !== undefined ? { fx: String(quotes.fx) } : {}),
        };
        setRaw((prev) => ({ ...prev, ...patch }));
        // A refreshed quote is not an edit the operator has to confirm.
        setCommitted((prev) => ({ ...prev, raw: { ...prev.raw, ...patch } }));
    }, []);
    const { status: quoteStatus, refresh } = (0, useMarketQuotes_1.useMarketQuotes)(applyQuotes);
    const handleFossilChange = (next) => {
        setFossil(next);
        // Reset to a typical price for the selected grade.
        setRaw((prev) => ({
            ...prev,
            priceFossil: String(model_1.FOSSIL_FUELS[next].price),
        }));
    };
    const handleFieldChange = (field, value) => setRaw((prev) => ({ ...prev, [field]: value }));
    const handleAsk = (topic) => {
        const next = topics.includes(topic) ? topics : [...topics, topic];
        setTopics(next);
        ctaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        focusTimer.current = window.setTimeout(() => emailRef.current?.focus({ preventScroll: true }), 400);
        setNote({
            text: `We will explain ${next
                .join(" and ")
                .toLowerCase()} when we come back to you. Just leave a contact below.`,
            warn: false,
        });
    };
    const handleFuelSend = async () => {
        if (!fuelEmail.trim() && !fuelPhone.trim()) {
            setFuelNote({
                text: "Add an email or a phone number so we can reach you.",
                warn: true,
            });
            return;
        }
        setFuelSendState("sending");
        setFuelNote({ text: "Sending your request…", warn: false });
        const ok = await (0, netlifyForms_1.submitBiofuelOffer)(inputs, result, {
            email: fuelEmail.trim(),
            phone: fuelPhone.trim(),
            imo: imo.trim(),
        });
        setFuelSendState(ok ? "sent" : "error");
        setFuelNote(ok
            ? {
                text: "Request received. We will come back to you within one business day.",
                warn: false,
            }
            : {
                text: `That did not send. Please try again, or write to ${model_1.CONTACT_EMAIL}.`,
                warn: true,
            });
    };
    const handleSend = async () => {
        if (!email.trim() && !phone.trim()) {
            setNote({
                text: "Add an email or a phone number so we can reach you.",
                warn: true,
            });
            emailRef.current?.focus();
            return;
        }
        setSendState("sending");
        setNote({ text: "Sending your request…", warn: false });
        const ok = await (0, netlifyForms_1.submitQuoteRequest)(inputs, result, {
            email: email.trim(),
            phone: phone.trim(),
            topics,
        });
        setSendState(ok ? "sent" : "error");
        setNote(ok
            ? {
                text: "Request received. We will come back to you with a price within one business day.",
                warn: false,
            }
            : {
                text: `That did not send. Please try again, or write to ${model_1.CONTACT_EMAIL}.`,
                warn: true,
            });
    };
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [report, (0, jsx_runtime_1.jsxs)(calculator_styles_1.Columns, { children: [(0, jsx_runtime_1.jsx)(InputsPanel_1.InputsPanel, { fossil: fossil, raw: raw, fuelCi: result.fuel.ci, fuelEf: result.fuel.ef, quoteStatus: quoteStatus, dirty: dirty, onPrint: () => window.print(), recording: consent === "granted", consentAnswered: consent !== "unset", onCalculate: handleCalculate, openHelp: openHelp, onFossilChange: handleFossilChange, onFieldChange: handleFieldChange, onToggleHelp: (topic) => setOpenHelp((open) => (open === topic ? null : topic)), onAsk: handleAsk, onRefresh: refresh }), (0, jsx_runtime_1.jsxs)("div", { children: [dirty && ((0, jsx_runtime_1.jsxs)(StaleNotice, { role: "status", children: [(0, jsx_runtime_1.jsx)(StaleDot, {}), (0, jsx_runtime_1.jsxs)("span", { children: [(0, jsx_runtime_1.jsx)("b", { children: "These figures are out of date." }), " They still show the last calculation \u2014 press Calculate to bring them up to date."] })] })), (0, jsx_runtime_1.jsxs)(Results, { "$stale": dirty, children: [(0, jsx_runtime_1.jsx)(HeadlineFigures_1.HeadlineFigures, { result: result }), (0, jsx_runtime_1.jsx)(LedgerPanel_1.LedgerPanel, { result: result })] }), (0, jsx_runtime_1.jsx)(QuoteRequestCta_1.QuoteRequestCta, { surplus: result.surplus, contact: {
                                    email,
                                    phone,
                                    onEmailChange: setEmail,
                                    onPhoneChange: setPhone,
                                }, note: note, submitState: sendState, sectionRef: ctaRef, emailRef: emailRef, onSend: handleSend }), (0, jsx_runtime_1.jsx)(BiofuelOfferCta_1.BiofuelOfferCta, { tons: inputs.tons, imo: imo, onImoChange: setImo, contact: {
                                    email: fuelEmail,
                                    phone: fuelPhone,
                                    onEmailChange: setFuelEmail,
                                    onPhoneChange: setFuelPhone,
                                }, note: fuelNote, submitState: fuelSendState, onSend: handleFuelSend }), (0, jsx_runtime_1.jsx)(SensitivityPanel_1.SensitivityPanel, { inputs: inputs }), (0, jsx_runtime_1.jsx)(Assumptions_1.Assumptions, { result: result })] })] })] }));
};
exports.SurplusCalculator = SurplusCalculator;
